import {
  Corridor,
  CorridorType,
  Train,
  TrainAwaitingDeparture,
  TrainLengthClassificationStatus
} from '../models/train-info.model';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { EventSourcePolyfill } from 'ng-event-source';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as _ from 'lodash';
import {
  SystemResponse,
  Terminal,
  CorridorLevel,
  TerminalLevel
} from '../models/train-info.model';

import {
  iohLevelTwoTerminalPositions,
  iohLevelTwoCorridorPositions
} from 'src/app/constants/ioh-level-two-contant';
import { AuthenticationService, Jwt } from '@csx/security';
import { IOHFilterFormService } from './filter/ioh-filter-form.service';
import { IOH_USER_PREFERENCES_STORAGE_KEY } from './user-preferences.service';
import {
  iohLevelOneTerminalPositions,
  iohLevelOneCorridorPositions,
  Terminals,
  MILEPOSTS
} from 'src/app/constants/ioh-level-one-contant';
import { MessageService } from 'primeng/api';
import { PopupClickPoint, positionTerminalPopup } from 'src/app/shared/utils/terminalPopupPosition';

@Injectable({
  providedIn: 'root'
})
export class IOHAppService {
  private readonly rtopApi = `${environment.appConfig.api.rtopApi}/rtop`;
  private readonly systemApi = `${environment.appConfig.api.intermodalOperationsApi}/schematic/trains-corridors`;
  private readonly gisApi = `${environment.appConfig.api.opsTrainApi}/gisData/rtopBBox`;
  private readonly regionsBboxApi = `${environment.appConfig.api.opsTrainApi}/gisData/regionsBBox`;
  private readonly zoneBboxApi = `${environment.appConfig.api.opsTrainApi}/gisData/zonesBBox`;
  private readonly L3TrainsApi = `${environment.appConfig.api.opsTrainApi}/trains/rtop?format=json`;
  private readonly streamingApi = `${environment.appConfig.api.streamingDashboardAssetsApi}/occ-dashboard`;
  private readonly gateInfoApi = `${environment.appConfig.api.intermodalOperationsApi}/gate-info/terminal-metrics`;
  private readonly IOtrainInfoApi = `${environment.appConfig.api.intermodalOperationsApi}/train-schedule`;
  private readonly opsTrainPortalApi = `${environment.appConfig.api.opsTrainApi}`;
  private readonly terminalInfoApi = `${environment.appConfig.api.intermodalOperationsApi}/terminal-info`;
  private readonly networkStatsApi = `${environment.appConfig.api.intermodalOperationsApi}/network-stats`;
  private serverSentEquipmentInfoEvent: EventSourcePolyfill;
  // Converted BehaviorSubjects to signals
  public iohLevelUpdate$ = signal<number>(0);
  public iohEquipmentInfo$ = signal<any>(null);
  public iohEquipmentInfoWc$ = signal<any>(null);
  public iohGateInfo$ = signal<any>(null);
  public iohTrainInfo$ = signal<any>(null);
  public iohTableZIndexLevels: number[] = [];
  public iohSystemResponse: SystemResponse;
  public iohTrainsMap: Map<string, Train> = new Map();
  public IohTrainsMeetingFilterCriteria: Train[] = null;
  public iohTerminalsFromApi = [];
  public iohCorridorsFromApi = [];
  public iohTerminalInfoFromApi: Terminal[] = [];
  public iohTerminalInfoList: any[] = [];
  public iohTerminalInfoRaw$ = signal<any[]>([]);
  public iohNetworkStats$ = signal<any>(null);
  public iohTerminalsLevels: TerminalLevel[][] = [];
  public iohCorridorsLevels: CorridorLevel[][] = [];
  public iohl3Trains: Train[] = [];
  public iohl3Trains$ = signal<Train[]>([]);
  public iohTerminalsLevels$ = signal<TerminalLevel[][]>([]);
  public iohCorridorsLevel$ = signal<CorridorLevel[][]>([]);
  public intermodalMask$ = signal<boolean>(false);
  isClosedByError: boolean = false;
  public iohFilterTrainsCountByCategory = {
    [TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_1]: 0,
    [TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_2]: 0,
    [TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_3]: 0,
    [TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_4]: 0,
    [TrainLengthClassificationStatus.REPORTED_LENGTH_CLASSIFICATION_5]: 0,
    PERFORMANCE_CLASSIFICATION_LATE: 0,
    PERFORMANCE_CLASSIFICATION_DELAYED: 0,
    PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME: 0,
    PERFORMANCE_CLASSIFICATION_EARLY: 0,
    PERFORMANCE_CLASSIFICATION_UNSPECIFIED: 0,
    TOTAL_TRAIN_GROUPS: 0,
    TOTAL_UPS_TRAINS: 0,
    TRAIN_STOPPED: 0,
    PERFORMANCE_CLASSIFICATION_LATE_2: 0,
    PERFORMANCE_CLASSIFICATION_LATE_4: 0,
    PERFORMANCE_CLASSIFICATION_LATE_8: 0,
    PERFORMANCE_CLASSIFICATION_LATE_12: 0,
  };
  public _iohActiveTrains = signal([[], []]);
  public iohGisDataLoaded$ = signal<any>({
    gisLoaded: false,
    gisRegionsLoaded: false,
    gisZonesLoaded: false
  });

  public iohGetFilterTrainsCountByCategory() {
    const commodityObj = {};
    this.iohFilterFormService.commodityFilterOptions.forEach(el => {
      commodityObj[el.label] = 0;
    });
    return {
      ...this.iohFilterTrainsCountByCategory,
      ...commodityObj
    };
  }

  public iohFilterTrainsCountByLevel = [
    this.iohGetFilterTrainsCountByCategory(),
    this.iohGetFilterTrainsCountByCategory(),
    this.iohGetFilterTrainsCountByCategory()
  ];

  public readonly iohIsTrainIdSearch = signal('');

  iohSetTrainIdForSearch(trainId) {
    this.iohIsTrainIdSearch.set(trainId);
  }

  public readonly iohLevelChangeEventTrigger = signal('');

  iohSetLevelToNavigate(level) {
    this.iohLevelChangeEventTrigger.set(level);
  }

  public readonly iohFontSizeValue$ = signal('12');

  iohSetfontSizeValue(fontSize) {
    this.iohFontSizeValue$.set(fontSize + 'px');
  }

  constructor(
    private http: HttpClient,
    private iohFilterFormService: IOHFilterFormService,
    private authService: AuthenticationService,
    private messageService: MessageService,
  ) {}

  iohResetFilterTrainsCountCategory() {
    this.iohFilterTrainsCountByLevel = [
      this.iohGetFilterTrainsCountByCategory(),
      this.iohGetFilterTrainsCountByCategory(),
      this.iohGetFilterTrainsCountByCategory()
    ];
  }

  /**
   * Retrieves the system using an HTTP GET request.
   *
   * @return {Observable<any>} An observable that emits the response from the rest API.
   */
  getIohSystem(): Observable<any> {
    return this.http.get(this.systemApi).pipe(catchError(this.handleError));
  }

  getIohGateInfo(): Observable<any> {
    return this.http.get(this.gateInfoApi).pipe(catchError(this.handleError));
  }

  getIohIOTrainInfo(): Observable<any> {
    const mileposts: any = MILEPOSTS;
    return this.http
      .post(this.IOtrainInfoApi, {
        mileposts,
        duration: 30
      })
      .pipe(catchError(this.handleError));
  }

  getIohL3Trains(): Observable<any> {
    return this.http.get(this.L3TrainsApi).pipe(catchError(this.handleError));
  }

  getIohTerminalInfo(fetchTerminalInfo = true): Observable<any> {
    if (
      !fetchTerminalInfo &&
      this.iohTerminalInfoList &&
      this.iohTerminalInfoList.length > 0
    ) {
      return of(this.iohTerminalInfoList);
    }
    return this.http
      .get(this.terminalInfoApi)
      .pipe(catchError(this.handleError));
  }

  getIohNetworkStats(): Observable<any> {
    return this.http
      .get(this.networkStatsApi)
      .pipe(catchError(this.handleError));
  }

  iohBuildTerminalsFromApi(apiTerminals: any[]): Terminal[] {
    this.iohTerminalInfoList = apiTerminals || [];
    this.iohTerminalInfoRaw$.set(this.iohTerminalInfoList);
    if (!apiTerminals || apiTerminals.length === 0) {
      return [];
    }
    const allPositions = [
      ...iohLevelOneTerminalPositions,
      ...iohLevelTwoTerminalPositions
    ];
    return apiTerminals.map(apiTerminal => {
      const positionAbbrevation = this.iohNormalizeTerminalCode(
        apiTerminal.code
      );
      const position = allPositions.find(
        pos => pos.abbrevation === positionAbbrevation
      );
      return {
        uid: position?.uid ?? apiTerminal.code,
        // stored canonical so downstream raw-code comparisons match legacy NWO/GRE terminals
        code: `${positionAbbrevation}TRM`,
        trainAwaitingDeparture: []
      };
    });
  }

  getGisData(): Observable<any> {
    return this.http.get(this.gisApi);
  }

  getRegionsBboxData(): Observable<any> {
    return this.http
      .get(this.regionsBboxApi)
      .pipe(catchError(this.handleError));
  }

  getZoneBboxData(): Observable<any> {
    return this.http.get(this.zoneBboxApi).pipe(catchError(this.handleError));
  }

  getIohTrainCommodities(fetchCommodities = true): Observable<any> {
    if (
      !fetchCommodities &&
      this.iohFilterFormService.iohCommodityAllFilterOptions &&
      this.iohFilterFormService.iohCommodityAllFilterOptions.length > 0
    ) {
      return of(this.iohFilterFormService.iohCommodityAllFilterOptions);
    }
    return this.http
      .get<any>(`${this.opsTrainPortalApi}/trains/traintypecommodity`)
      .pipe(
        map(commoditys =>
          commoditys.map(commodity => ({
            label: commodity.commodityDesc,
            value: commodity.commodityDesc,
            filterKey: commodity.commodityDesc,
            ...commodity,
            color: '#B3CDFB'
          }))
        ),
        catchError(err => {
          this.iohFilterFormService.iohCommodityApiErrorCode = err?.status;
          return of([]);
        })
      );
  }

  /**
   * populate level terminals and corridors with the
   * filtered trains.
   */
  iohPopulateLevelTerminalAndCorridors() {
    this.iohResetFilterTrainsCountCategory();
    this.iohTerminalsLevels = [];
    this.iohCorridorsLevels = [];
    this.iohLevelTerminals(
      [iohLevelOneTerminalPositions, iohLevelTwoTerminalPositions],
      this.iohTerminalsFromApi,
      this.iohCorridorsFromApi
    );

    this.iohLevelCorridors(
      [iohLevelOneCorridorPositions, iohLevelTwoCorridorPositions],
      this.iohCorridorsFromApi
    );

    this.getIohL3TrainsData();
    this.iohTerminalsLevels$.set(this.iohTerminalsLevels);
    this.iohCorridorsLevel$.set(this.iohCorridorsLevels);
  }

  getIohL3TrainsData() {
    const l2Trains: any[] = [];
    this.iohTerminalsLevels[1].forEach(terminal => {
      l2Trains.push(...terminal.trainOrders);
    });

    this.iohCorridorsLevels[1].forEach(corridor => {
      l2Trains.push(...corridor.trainOrders);
    });

    const iohl3Trains = this.iohFilterTrains(l2Trains)?.filter(
      train => train.latitude !== undefined && train.latitude !== null
    );

    const l3FilteredTrains = this.iohFilterCorridorTrainsAsPerCriteria(
      [...iohl3Trains],
      2
    );
    const l2TerminalTrains: any[] = [];
    this.iohTerminalsLevels[1].forEach(terminal => {
      l2TerminalTrains.push(...terminal.trainAwaitingDeparture);
    });
    const l3TerminalTrains = this.iohFilterTrains(
      l2TerminalTrains,
      true
    )?.filter(train => train.latitude !== undefined && train.latitude !== null);
    const l3FilteredTerminalTrains = this.iohFilterTerminalTrainsAsPerCriteria(
      [...l3TerminalTrains],
      2
    ).map(train => {
      train.inTerminal = true;
      return train;
    });
    this.iohl3Trains = [...l3FilteredTrains, ...l3FilteredTerminalTrains];
    queueMicrotask(() => {
      this.iohl3Trains$.set(this.iohl3Trains);
    });
  }

  iohUpdateTerminalAndCorridor() {
    this.iohResetFilterTrainsCountCategory();
    this.iohTerminalsLevels.forEach((terminalLevel, levelIndex) => {
      terminalLevel.forEach(terminal => {
        const terminalFromAPI = this.iohTerminalsFromApi.find(
          ter =>
            ter.uid === terminal.uid &&
            terminal.code.replace('TRM', '') === ter.code.replace('TRM', '')
        );
        terminal.trainAwaitingDeparture =
          terminalFromAPI?.trainAwaitingDeparture || [];
        const awaitingDepartureTrains = this.iohFilterTrains(
          terminal.trainAwaitingDeparture,
          true
        );
        terminal.awaitingDepartureTrains =
          this.iohFilterTerminalTrainsAsPerCriteria(
            awaitingDepartureTrains,
            levelIndex
          );
        terminal.isUpdatedByFilter = false;

        const corridorFromAPI = this.iohCorridorsFromApi.find(
          corridor =>
            corridor.type === CorridorType.CORRIDOR_TYPE_TERMINAL &&
            Number(corridor.gisSchematicLevel) - 1 === levelIndex &&
            corridor.code.replace('TRM', '') ===
              this.iohNormalizeTerminalCode(terminal.code.replace('TRM', ''))
        );

        terminal.trainOrders = corridorFromAPI?.trainOrders || [];
        const nearByTrains = this.iohFilterTrains(terminal.trainOrders);
        terminal.nearByTrains = this.iohFilterCorridorTrainsAsPerCriteria(
          nearByTrains,
          levelIndex
        );
      });
    });

    this.iohTerminalsLevels$.set(this.iohTerminalsLevels);
    this.iohCorridorsLevel$.set(this.iohCorridorsLevels);

    this.iohCorridorsLevels.forEach((corridorLevel, levelIndex) => {
      corridorLevel.forEach(corridor => {
        const corridorFromAPI = this.iohCorridorsFromApi.find(
          cor =>
            cor.type === CorridorType.CORRIDOR_TYPE_CORRIDOR &&
            corridor.departing === cor.departing &&
            corridor.approaching === cor.approaching
        );

        corridor.trainOrders = corridorFromAPI?.trainOrders || [];
        const trains = this.iohFilterTrains(corridor.trainOrders);
        corridor.trains = this.iohFilterCorridorTrainsAsPerCriteria(
          trains,
          levelIndex
        );
      });
    });

    this.getIohL3TrainsData();
  }

  iohIsTrainPerformanceMatched(train, filterCriteria) {
    const skipFilter = filterCriteria.performance.every(
      performance => !performance || !performance[0]
    );

    // Check if Late is selected in performance filters
    const lateSelected = filterCriteria.performance.some(
      performance => performance && performance[0] === 'PERFORMANCE_CLASSIFICATION_LATE'
    );

    // Check if any late sub-filters are selected
    const lateSubFiltersSelected = filterCriteria.lateSubFilters && filterCriteria.lateSubFilters.some(
      subFilter => subFilter && subFilter[0]
    );

    // If Late is selected and sub-filters are also selected, check sub-filter criteria
    if (lateSelected && lateSubFiltersSelected) {
      const trainLateMinutes = Number(this.transformTime(train.lastReportedPerformance));
      
      const isLateSubFilterMatched = filterCriteria.lateSubFilters.some(subFilter => {
        if (!subFilter || !subFilter[0]) return false;
        
        const filterKey = subFilter[0];
        switch (filterKey) {
          case 'PERFORMANCE_CLASSIFICATION_LATE_2':
            return trainLateMinutes >= 0 && trainLateMinutes <= 240;
          case 'PERFORMANCE_CLASSIFICATION_LATE_4':
            return trainLateMinutes > 240 && trainLateMinutes <= 480;
          case 'PERFORMANCE_CLASSIFICATION_LATE_8':
            return trainLateMinutes > 480 && trainLateMinutes <= 720;
          case 'PERFORMANCE_CLASSIFICATION_LATE_12':
            return trainLateMinutes > 720;
          default:
            return false;
        }
      });

      // Train matches if it's Late AND matches at least one sub-filter
      if (train.performanceClassification === 'PERFORMANCE_CLASSIFICATION_LATE') {
        return isLateSubFilterMatched;
      }
      // For non-Late trains, check if they match any other selected performance filter
      return filterCriteria.performance.some(performance => {
        return performance && performance[0] === train.performanceClassification;
      });
    }

    // Original logic when Late is not selected or no sub-filters selected
    const isFilterMatched =
      skipFilter ||
      filterCriteria.performance.some(performance => {
        return (
          performance && performance[0] === train.performanceClassification
        );
      });
    return isFilterMatched;
  }

  iohIsCommodityMatched(train, filterCriteria) {
    const commodityDesc = this.iohFilterFormService.resolveTrainCommodity(train);
    const skipFilter = filterCriteria.commodities.every(
      commoditie => !commoditie || !commoditie[0]
    );
    const isFilterMatched =
      skipFilter ||
      filterCriteria.commodities.some(commoditie => {
        return commoditie && commoditie[0] === commodityDesc;
      });
    return isFilterMatched;
  }

  iohIsTrainGroupMatched(train, filterCriteria) {
    if (filterCriteria.trainGroups === '') {
      return true;
    }
    return (
      train.trainGroups.includes(filterCriteria.trainGroups) || train.upsTrain
    );
  }

  isTrainStoppageMatched(train: Train, filterCriteria: any) {
    const skipFilter = filterCriteria.trainStoppage.every(stoppage => !stoppage || !stoppage[0]);
    const isFilterMatched =
      skipFilter ||
      filterCriteria.trainStoppage.some(stoppage => {
        return stoppage && stoppage[0] && stoppage[0] === 'TRAIN_STOPPED' && train.trainStopped && train.stoppedTimeInMinutes && train.stoppedTimeInMinutes > 120;
      });
    return isFilterMatched;
  }

  isTrainLengthMatched(train, filterCriteria) {
    const skipFilter = filterCriteria.trainLengths.every(
      trainLength => !trainLength || !trainLength[0]
    );
    const isFilterMatched =
      skipFilter ||
      filterCriteria.trainLengths.some(trainLength => {
        return trainLength && trainLength[0] === train.reportedLengthClassification;
      });
    return isFilterMatched;
  }

  /**
   * filter the trains and update level terminal
   * and corridor trins
   */
  iohFilterTrainsByCriteria(filterCriteria) {
    this.iohFilterTerminalsAndCorridors();
  }

  iohResetTerminalPopupState(): void {
    this.iohTerminalsLevels.forEach(terminalLevel => {
      terminalLevel.forEach(terminal => {
        terminal.showEquipmentInfo = false;
        terminal.showGateInfo = false;
        terminal.showTrainInfo = false;
        terminal.showNearByTrains = false;
      });
    });
    this.iohTerminalsLevels$.set(this.iohTerminalsLevels);
  }

  correctOpenPopupAnchors(deltaX: number, deltaY: number, showFilter: boolean): void {
    if (!deltaX && !deltaY) return;
    this.iohTerminalsLevels.forEach(terminalLevel => {
      terminalLevel.forEach(terminal => {
        if (
          !terminal.popupClickPoint ||
          !(terminal.showEquipmentInfo || terminal.showGateInfo || terminal.showTrainInfo)
        ) {
          return;
        }
        const point = terminal.popupClickPoint as PopupClickPoint;
        terminal.popupClickPoint = { ...point, x: point.x + deltaX, y: point.y + deltaY };
        if (terminal.popupBounds) {
          terminal.popupBounds = {
            left: terminal.popupBounds.left + deltaX,
            right: terminal.popupBounds.right + deltaX,
            top: terminal.popupBounds.top + deltaY,
            bottom: terminal.popupBounds.bottom + deltaY
          };
        }
        const element = document.getElementById(terminal.uid);
        if (element) {
          positionTerminalPopup(element, terminal.uid, showFilter, terminal.popupClickPoint, terminal.popupBounds ?? null);
        }
      });
    });
  }

  iohPrepopulateData(data: SystemResponse, isPolling = false) {
    // preserve saved default filter preferences across refreshes/polling
    const savedUserPreferences = localStorage.getItem(IOH_USER_PREFERENCES_STORAGE_KEY);
    localStorage.clear();
    if (savedUserPreferences !== null) {
      localStorage.setItem(IOH_USER_PREFERENCES_STORAGE_KEY, savedUserPreferences);
    }
    this.iohTrainsMap = new Map();
    for (const train of data?.trains) {
      this.iohTrainsMap.set(train.trainId, train);
    }
    const terminals =
      this.iohTerminalInfoFromApi.length > 0
        ? this.iohTerminalInfoFromApi
        : Terminals;
    for (const terminal of terminals) {
      for (const awaitingDeparture of terminal.trainAwaitingDeparture) {
        const trainId = awaitingDeparture.train6;
        const train = this.iohTrainsMap.get(trainId);
        if (train) {
          train.activeMilepostInfo =
            awaitingDeparture.originTransportationMilepost;
        }
      }
    }
    this.iohTerminalsFromApi = terminals;
    this.iohCorridorsFromApi = data.corridors;
    isPolling
      ? this.iohUpdateTerminalAndCorridor()
      : this.iohPopulateLevelTerminalAndCorridors();
    this.getIohActiveTrains();
  }

  iohFindTerminalByAbbreviation(
    terminals: Terminal[],
    abbreviation: string,
    uid: string
  ): Terminal | undefined {
    return terminals.find(
      terminal =>
        terminal.code.replace('TRM', '') === abbreviation &&
        terminal.uid === uid
    );
  }

  /**
   * filter the trains based on the filter criteria.
   * @param trains
   * @returns
   */
  iohFilterCorridorTrainsAsPerCriteria(trains, levelIndex) {
    const IohTrainsMeetingFilterCriteria = trains?.filter(train => {
      const commodityDesc = this.iohFilterFormService.resolveTrainCommodity(train);
      let trainIdMatched = true;
      if (
        this.iohFilterFormService.iohFilterFormGroup.value.iohInputForSearch
      ) {
        trainIdMatched =
          train.trainId?.toLowerCase() ===
          this.iohFilterFormService.iohFilterFormGroup.value.iohInputForSearch?.toLowerCase();
        return trainIdMatched;
      }

      if (
        train.performanceClassification ===
        'PERFORMANCE_CLASSIFICATION_UNSPECIFIED'
      ) {
        return trainIdMatched;
        /**
         * ignore PERFORMANCE_CLASSIFICATION_UNSPECIFIED from the calculation but should
         * be considered in the map.
         */
      }
      const trainPerformanceMatched = this.iohIsTrainPerformanceMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      const trainCommodityMatched = this.iohIsCommodityMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      const trainGroupMatched = this.iohIsTrainGroupMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      const trainStoppedMatched = this.isTrainStoppageMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      const trainLengthMatched = this.isTrainLengthMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      if (trainCommodityMatched && trainIdMatched && trainGroupMatched && trainStoppedMatched && trainLengthMatched) {
        this.iohFilterTrainsCountByLevel[levelIndex][
          train.performanceClassification
        ] += 1;
       if(train.status == 'Late'){
        if(Number(this.transformTime(train.lastReportedPerformance)) >= 0 && Number(this.transformTime(train.lastReportedPerformance)) <= 240){
            this.iohFilterTrainsCountByLevel[levelIndex][
          'PERFORMANCE_CLASSIFICATION_LATE_2'
        ] += 1;
        }
        if(Number(this.transformTime(train.lastReportedPerformance)) > 240 && Number(this.transformTime(train.lastReportedPerformance)) <= 480){
            this.iohFilterTrainsCountByLevel[levelIndex][
          'PERFORMANCE_CLASSIFICATION_LATE_4'
        ] += 1;
        }
        if(Number(this.transformTime(train.lastReportedPerformance)) > 480 && Number(this.transformTime(train.lastReportedPerformance)) <= 720){
            this.iohFilterTrainsCountByLevel[levelIndex][
          'PERFORMANCE_CLASSIFICATION_LATE_8'
        ] += 1;
        }
        if(Number(this.transformTime(train.lastReportedPerformance)) > 720){
            this.iohFilterTrainsCountByLevel[levelIndex][
          'PERFORMANCE_CLASSIFICATION_LATE_12'
        ] += 1;
        }
      }
      }
      if (commodityDesc && trainPerformanceMatched && trainIdMatched && trainGroupMatched && trainStoppedMatched && trainLengthMatched) {
        this.iohFilterTrainsCountByLevel[levelIndex][commodityDesc] += 1;
      }

      if (trainCommodityMatched && trainIdMatched && trainPerformanceMatched && trainStoppedMatched && trainLengthMatched) {
        this.iohFilterTrainsCountByLevel[levelIndex]['TOTAL_TRAIN_GROUPS'] += 1;
        train.trainGroups.forEach(group => {
          this.iohFilterTrainsCountByLevel[levelIndex][group] += 1;
        });
        if (train.upsTrain) {
          this.iohFilterTrainsCountByLevel[levelIndex]['TOTAL_UPS_TRAINS'] += 1;
        }
      }

      if (
        trainPerformanceMatched &&
        (train.trainStopped && train.stoppedTimeInMinutes && train.stoppedTimeInMinutes > 120)  && 
        trainCommodityMatched &&
        trainGroupMatched && 
        trainIdMatched && trainLengthMatched
      ) {
        this.iohFilterTrainsCountByLevel[levelIndex]['TRAIN_STOPPED'] += 1;
      }

      if (
        trainPerformanceMatched &&
        trainStoppedMatched &&
        trainCommodityMatched &&
        trainGroupMatched &&
        trainIdMatched
      ) {
        this.iohFilterTrainsCountByLevel[levelIndex][train.reportedLengthClassification] += 1;
      }


      return (
        trainPerformanceMatched &&
        trainCommodityMatched &&
        trainIdMatched &&
        trainStoppedMatched && trainLengthMatched &&
        trainGroupMatched
      );
    });
    return IohTrainsMeetingFilterCriteria;
  }

  iohFilterTerminalTrainsAsPerCriteria(trains, levelIndex) {
    const IohTrainsMeetingFilterCriteria = trains?.filter(train => {
      let trainIdMatched = true;
      if (
        this.iohFilterFormService.iohFilterFormGroup.value.iohInputForSearch
      ) {
        trainIdMatched =
          train.trainId?.toLowerCase() ===
          this.iohFilterFormService.iohFilterFormGroup.value.iohInputForSearch?.toLowerCase();
        return trainIdMatched;
      }

      const trainPerformanceMatched = this.iohIsTrainPerformanceMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      const trainCommodityMatched = this.iohIsCommodityMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      const trainGroupMatched = this.iohIsTrainGroupMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      const trainStoppedMatched = this.isTrainStoppageMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      const trainLengthMatched = this.isTrainLengthMatched(
        train,
        this.iohFilterFormService.iohFilterFormGroup.getRawValue()
      );

      return (
        trainPerformanceMatched &&
        trainCommodityMatched &&
        trainIdMatched &&
        trainGroupMatched && trainStoppedMatched && trainLengthMatched
      );
    });
    return IohTrainsMeetingFilterCriteria;
  }

  private readonly iohLegacyTerminalCodeMap: Record<string, string> = {
    NWO: 'NOW',
    GRE: 'PHI'
  };

  iohNormalizeTerminalCode(code: string): string {
    return this.iohLegacyTerminalCodeMap[code] || code;
  }

  iohLevelTerminals(
    terminalPositionsByLevel: any[],
    allTerminals: Terminal[],
    allCorridors: Corridor[]
  ) {
    terminalPositionsByLevel.forEach(
      (levelTerminalPosition: any, levelIndex: number) => {
        const iohLevelTerminals: TerminalLevel[] =
          levelIndex === 0 && this.iohTerminalInfoList.length > 0
            ? this.iohBuildLevelOneTerminalsFromApi(allTerminals, allCorridors)
            : this.iohBuildTerminalsFromPositions(
                levelTerminalPosition,
                levelIndex,
                allTerminals,
                allCorridors
              );

        this.iohTerminalsLevels.push(iohLevelTerminals);
        this.iohTableZIndexLevels.push(5);
      }
    );
  }

  iohBuildLevelOneTerminalsFromApi(
    allTerminals: Terminal[],
    allCorridors: Corridor[]
  ): TerminalLevel[] {
    const levelIndex = 0;
    return this.iohTerminalInfoList.map(apiTerminal => {
      const positionAbbrevation = this.iohNormalizeTerminalCode(
        apiTerminal.code
      );
      const terminalPosition = iohLevelOneTerminalPositions.find(
        pos => pos.abbrevation === positionAbbrevation
      );

      const terminal = this.iohFindTerminalByAbbreviation(
        allTerminals,
        positionAbbrevation,
        terminalPosition?.uid
      );

      const corridor = allCorridors.find(
        corridor =>
          corridor.type === CorridorType.CORRIDOR_TYPE_TERMINAL &&
          Number(corridor.gisSchematicLevel) - 1 === levelIndex &&
          corridor.code.replace('TRM', '') === positionAbbrevation
      );

      const awaitingDepartureTrains = terminal
        ? this.iohFilterTrains(terminal.trainAwaitingDeparture, true)
        : [];
      const filteredAwaitingDepartureTrains =
        this.iohFilterTerminalTrainsAsPerCriteria(
          awaitingDepartureTrains,
          levelIndex
        );

      const nearByTrains = corridor
        ? this.iohFilterTrains(corridor.trainOrders)
        : [];
      const filteredNearByTrains = this.iohFilterCorridorTrainsAsPerCriteria(
        nearByTrains,
        levelIndex
      );

      const terminalUid = terminal?.uid || terminalPosition?.uid || apiTerminal.code;

      return {
        uid: terminalUid,
        schematicId: terminalPosition?.schematicId ?? apiTerminal.code,
        milepost: terminalPosition?.milepost || apiTerminal.milepost,
        // canonical so it matches the normalized codes stored in iohTerminalsFromApi
        code: positionAbbrevation,
        name: apiTerminal.name,
        awaitingDepartureTrains: filteredAwaitingDepartureTrains,
        trainOrders: corridor ? corridor.trainOrders : [],
        nearByTrains: filteredNearByTrains,
        trainAwaitingDeparture: terminal
          ? this.iohFilterAwaitingDepartureTrain(terminal.trainAwaitingDeparture)
          : [],
        terminalPosition: {
          startX: terminalPosition?.startX ?? 0,
          startY: terminalPosition?.startY ?? 0
        },
        nearByTrainsDirection: terminalPosition?.direction ?? 0,
        showNearByTrains: false,
        nearByTrainsTableZIndex: 0,
        showEquipmentInfo: false,
        showTrainInfo: false,
        showGateInfo: false,
        awaitingDepartureTrainsZIndex: 0,
        isUpdatedByFilter: false
      } as TerminalLevel;
    });
  }

  iohBuildTerminalsFromPositions(
    levelTerminalPosition: any[],
    levelIndex: number,
    allTerminals: Terminal[],
    allCorridors: Corridor[]
  ): TerminalLevel[] {
    const iohLevelTerminals: TerminalLevel[] = [];

    levelTerminalPosition.forEach(terminalPosition => {
      const terminal = this.iohFindTerminalByAbbreviation(
        allTerminals,
        terminalPosition.abbrevation,
        terminalPosition.uid
      );

      const corridor = allCorridors.find(
        corridor =>
          corridor.type === CorridorType.CORRIDOR_TYPE_TERMINAL &&
          Number(corridor.gisSchematicLevel) - 1 === levelIndex &&
          corridor.code.replace('TRM', '') === terminalPosition.abbrevation
      );

      const awaitingDepartureTrains = terminal
        ? this.iohFilterTrains(terminal.trainAwaitingDeparture, true)
        : [];

      /*** apply filtercriteria  */
      const filteredAwaitingDepartureTrains =
        this.iohFilterTerminalTrainsAsPerCriteria(
          awaitingDepartureTrains,
          levelIndex
        );

      const nearByTrains = corridor
        ? this.iohFilterTrains(corridor.trainOrders)
        : [];

      /*** apply filtercriteria  */
      const filteredNearByTrains =
        this.iohFilterCorridorTrainsAsPerCriteria(nearByTrains, levelIndex);

      const terminalUid =
        terminal && terminal.uid ? terminal.uid : terminalPosition.uid;

      iohLevelTerminals.push({
        uid: terminalUid,
        schematicId: terminalPosition?.schematicId,
        milepost: terminalPosition?.milepost,
        code: terminalPosition.abbrevation,
        name: terminalPosition.name,
        awaitingDepartureTrains: filteredAwaitingDepartureTrains,
        trainOrders: corridor ? corridor.trainOrders : [],
        nearByTrains: filteredNearByTrains,
        trainAwaitingDeparture: terminal
          ? this.iohFilterAwaitingDepartureTrain(
              terminal.trainAwaitingDeparture
            )
          : [],
        terminalPosition: {
          startX: terminalPosition.startX,
          startY: terminalPosition.startY
        },
        nearByTrainsDirection: terminalPosition.direction,
        showNearByTrains: false,
        nearByTrainsTableZIndex: 0,
        showEquipmentInfo: false,
        showTrainInfo: false,
        showGateInfo: false,
        awaitingDepartureTrainsZIndex: 0,
        isUpdatedByFilter: false
      });
    });

    return iohLevelTerminals;
  }

  iohLevelCorridors(corridorPositionsByLevel: any[], allCorridors: Corridor[]) {
    corridorPositionsByLevel.forEach(
      (levelCorridorPosition, levelIndex: number) => {
        const iohLevelCorridors: CorridorLevel[] = [];

        levelCorridorPosition.forEach(corridorPosition => {
          corridorPosition.approachingNodes.forEach(approachingNode => {
            const corridor = allCorridors.find(
              corridor =>
                corridor.departing === corridorPosition.abbreviation &&
                corridor.approaching === approachingNode.abbrevation
            );

            const trains = corridor
              ? this.iohFilterTrains(corridor.trainOrders)
              : [];

            const filteredTrains = this.iohFilterCorridorTrainsAsPerCriteria(
              trains,
              levelIndex
            );

            iohLevelCorridors.push({
              code: corridor ? corridor.code : '',
              approaching: approachingNode.abbrevation,
              departing: corridorPosition.abbreviation,
              gisSchematicLevel: (levelIndex + 1).toString(),
              name: corridor ? corridor.name : '',
              trainOrders: corridor ? corridor.trainOrders : [],
              type: CorridorType.CORRIDOR_TYPE_CORRIDOR,
              uid: corridor ? corridor.uid : '',
              trains: filteredTrains,
              tablePosition: approachingNode.tablePosition,
              arrowDirection: approachingNode.arrowDirection,
              showCount: approachingNode.trainCount,
              trainsZindex: 0
            });
          });
        });
        iohLevelCorridors.sort(
          (a, b) => a.tablePosition.sortOrder - b.tablePosition.sortOrder
        );
        this.iohCorridorsLevels.push(iohLevelCorridors);
      }
    );
  }

  getLevelCorridorPosition(level: string) {
    switch (level) {
      case '1':
        return iohLevelOneCorridorPositions;
      case '2':
        return iohLevelTwoCorridorPositions;
      default:
        return iohLevelOneCorridorPositions;
    }
  }

  getTablePositionByGivenLevel(
    levelCorridorPosition: any,
    fromNode: string,
    approachingNode: string
  ) {
    const corridor = levelCorridorPosition.find(
      corridor => corridor.abbreviation === fromNode
    );

    return (
      corridor?.approachingNodes.find(
        ap => ap.abbrevation === approachingNode
      ) || null
    );
  }

  iohFilterTrains(iohFilterTrains: any[], isTerminal: boolean = false): any[] {
    const filteredTrains: any[] = [];

    iohFilterTrains?.forEach(filterTrain => {
      if (this.iohTrainsMap.has(filterTrain.train6)) {
        const filteredTrain: Train = this.iohTrainsMap.get(filterTrain.train6);
        filteredTrains.push(filteredTrain);
      }
    });
    if (isTerminal) {
      return this.sortIohTerminalTrains(filteredTrains);
    }
    return filteredTrains;
  }

  sortIohTerminalTrains(trains: Train[]) {
    return trains.sort((train1, train2) => {
      if (
        train1.originTransportationMilepost ===
        train2.originTransportationMilepost
      ) {
        return (
          new Date(train1.scheduledDepartureTimeStamp).getTime() -
          new Date(train2.scheduledDepartureTimeStamp).getTime()
        );
      }
      return train1.originTransportationMilepost <
        train2.originTransportationMilepost
        ? -1
        : 1;
    });
  }

  iohFilterAwaitingDepartureTrain(trains: TrainAwaitingDeparture[]) {
    return trains;
  }

  iohUpdateCorridor(updateCorridor: Corridor) {
    const corridorLevel = this.iohCorridorsLevels[
      Number(updateCorridor.gisSchematicLevel) - 1
    ].find(corridor => {
      return (
        corridor.approaching === updateCorridor.approaching &&
        corridor.departing === updateCorridor.departing
      );
    });
    if (corridorLevel) {
      corridorLevel.trainOrders = updateCorridor.trainOrders;
      corridorLevel.trains = this.iohFilterTrains(corridorLevel.trainOrders);
      corridorLevel.trains = this.iohFilterCorridorTrainsAsPerCriteria(
        corridorLevel.trains,
        Number(updateCorridor.gisSchematicLevel) - 1
      );

      this.iohFilterTerminalsAndCorridors();
    }
  }

  iohUpdateNearByTrains(updateCorridor: Corridor) {
    const terminalLevel = this.iohTerminalsLevels[
      Number(updateCorridor.gisSchematicLevel) - 1
    ].find(terminalLevel => {
      return (
        terminalLevel.code.replace('TRM', '') ===
        updateCorridor.code.replace('TRM', '')
      );
    });
    if (terminalLevel) {
      terminalLevel.trainOrders = updateCorridor.trainOrders;
      terminalLevel.nearByTrains = this.iohFilterTrains(
        terminalLevel.trainOrders
      );
      terminalLevel.nearByTrains = this.iohFilterCorridorTrainsAsPerCriteria(
        terminalLevel.nearByTrains,
        Number(updateCorridor.gisSchematicLevel) - 1
      );

      this.iohFilterTerminalsAndCorridors();
    }
  }

  checkIfCommodityIsExist(train) {
    const commodityDesc = this.iohFilterFormService.resolveTrainCommodity(train);
    const comm = this.iohFilterFormService.commodityFilterOptions.find(com => {
      return com.commodityDesc === commodityDesc;
    });
    if (comm) {
      if (!comm['show']) {
        comm['show'] = true;
      }
    }
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const errorMessage = JSON.stringify(error?.error);
    return throwError(() => new Error(`Error: ${errorMessage}`));
  }

  iohFilterTerminalsAndCorridors() {
    this.iohResetFilterTrainsCountCategory();
    this.iohFilterTerminals();
    this.iohFilterCorridors();
    this.getIohL3TrainsData();
  }

  iohFilterTerminals() {
    const filteredTerminals = [];
    this.iohTerminalsLevels.forEach((terminalLevel, levelIndex) => {
      const filteredTerminal = terminalLevel;
      filteredTerminal.forEach(terminal => {
        const nearByTrains = this.iohFilterTrains(terminal.trainOrders);
        terminal.nearByTrains = this.iohFilterCorridorTrainsAsPerCriteria(
          nearByTrains,
          levelIndex
        );

        const awaitingDepartureTrains = this.iohFilterTrains(
          terminal.trainAwaitingDeparture,
          true
        );
        terminal.awaitingDepartureTrains =
          this.iohFilterTerminalTrainsAsPerCriteria(
            awaitingDepartureTrains,
            levelIndex
          );
        terminal.isUpdatedByFilter = true;
      });
      filteredTerminals.push(filteredTerminal);
    });
    setTimeout(() => {
      this.iohTerminalsLevels$.set(filteredTerminals);
    }, 0);
  }

  iohFilterCorridors() {
    const filteredCorridors = [];
    this.iohCorridorsLevels.forEach((corridorLevel, levelIndex) => {
      const filteredCorridor = corridorLevel;
      filteredCorridor.forEach(corridor => {
        const trains = this.iohFilterTrains(corridor.trainOrders);
        corridor.trains = this.iohFilterCorridorTrainsAsPerCriteria(
          trains,
          levelIndex
        );
      });
      filteredCorridors.push(filteredCorridor);
    });
    setTimeout(() => {
      this.iohCorridorsLevel$.set(filteredCorridors);
    }, 0);
  }

  getIohActiveTrains() {
    const trainsInLevels = [[], []];
    this.iohCorridorsFromApi.forEach(corridor => {
      corridor.trainOrders.forEach(train => {
        if (this.iohTrainsMap.has(train.train6)) {
          trainsInLevels[Number(corridor.gisSchematicLevel) - 1].push(
            train.train6
          );
        }
      });
    });

    this.iohTerminalsFromApi.forEach(terminal => {
      let level = 2;
      if (
        iohLevelOneTerminalPositions.find(
          terminalPosition => terminalPosition.uid === terminal.uid
        )
      ) {
        level = 1;
      }

      terminal.trainAwaitingDeparture.forEach(train => {
        if (this.iohTrainsMap.has(train.train6)) {
          trainsInLevels[level - 1].push(train.train6);
        }
      });
    });

    trainsInLevels.forEach(trainList => {
      trainList.sort();
    });
    // return trainsInLevels;
    setTimeout(() => {
      this._iohActiveTrains.set(trainsInLevels);
    }, 0);
  }

  /** Streaming API */
  createEventSourceObserver(): Observable<any> {
    return new Observable<any>(observer => {
      this.initializeSSE(
        `${this.streamingApi}`,
        this.getAccessToken(),
        observer
      );
    });
  }

  private initializeSSE(url: string, accessToken: string, observer: any): void {
    this.serverSentEquipmentInfoEvent = new EventSourcePolyfill(url, {
      headers: {
        Authorization: 'Bearer ' + accessToken,
        'Content-Type': 'text/event-stream'
      }
    });

    this.serverSentEquipmentInfoEvent.onmessage = event => {
      if (event.data != 'Keep-Alive') {
        const eventData = JSON.parse(event.data);

        if (eventData.error) {
          this.isClosedByError = true;
          this.closeSSE(observer);
        } else {
          // Defer update to next microtask to avoid "changed after checked" in zoneless mode
          setTimeout(() => {
            observer.next(eventData);
            // Additional update: emit to the equipment info subject for data propagation
            this.iohEquipmentInfo$.set(eventData);
          }, 0);
        }
      }
    };

    this.serverSentEquipmentInfoEvent.onerror = error => {
      // if (!this.isClosedByError) {
      //   this.messageService.add({
      //     key: 'equipmentApiError',
      //     severity: 'error',
      //     summary: 'Error',
      //     detail: `Equipment data failed to load. Please retry after some time.`
      //   });
      // }
      this.isClosedByError = true;
      this.closeSSE(observer);
    };
  }

  closeSSE(observer: any) {
    if (this.serverSentEquipmentInfoEvent) {
      this.serverSentEquipmentInfoEvent.close();
      this.serverSentEquipmentInfoEvent = null;

      if (this.isClosedByError) {
        setTimeout(() => {
          this.isClosedByError = false;
          const accessToken = JSON.parse(
            eval(
              'window.sessionStorage.authorizationResult__' +
                environment.appConfig.security.clientId
            )
          ).access_token;
          this.initializeSSE(`${this.streamingApi}`, accessToken, observer);
        }, 5000);
      }
    }
  }

  killSSE() {
    if (this.serverSentEquipmentInfoEvent) {
      this.serverSentEquipmentInfoEvent.close();
      this.serverSentEquipmentInfoEvent = null;
    }
  }

  private getAccessToken(): string {
    let token: string | null = null;
    let accessToken = (<any>this.authService).getAccessToken();
    if (accessToken && accessToken.hasOwnProperty('encoded')) {
      token = (<Jwt>accessToken).encoded;
    } else {
      token = <string>accessToken;
    }
    if (token == null) {
      throw new Error('System does not have an access token.');
    }
    return token;
  }

  transformTime(value: string): string {
    if (!value) {
      return '0 min';
    }

    let totalHours = 0;
    let minutes = 0;

    const daysMatch = value.match(/(\d+)d/);
    const hoursMatch = value.match(/(\d+)h/);
    const minutesMatch = value.match(/(\d+)m/);

    if (daysMatch) {
      totalHours += parseInt(daysMatch[1], 10) * 24;
    }
    if (hoursMatch) {
      totalHours += parseInt(hoursMatch[1], 10);
    }
    if (minutesMatch) {
      minutes += parseInt(minutesMatch[1], 10);
    }

    if (totalHours > 0 && minutes > 0) {
      let totalminutes= (totalHours*60) + minutes;
      return `${totalminutes}`;
    } else if (totalHours > 0) {
      let totalminutes= (totalHours*60);
      return `${totalminutes}`;
    } else if (minutes > 0) {
      return `${minutes}`;
    }
    return '0';
  }
}
